import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { useApi, publicApi, API } from "../hooks/useApi";
import { AskAboutThis } from "../components/AskAboutThis";
import { AskMrE } from "../components/AskMrE";
import { PdfViewer } from "../components/PdfViewer";
import { toast } from "sonner";
import { Minus, Plus, Bookmark, X } from "lucide-react";

const READING_THEMES = {
  dark: { bg: "#0A0A0A", text: "#F2EFE9" },
  sepia: { bg: "#F4ECD8", text: "#3A2E1E" },
  light: { bg: "#FAFAFA", text: "#111111" },
};

function QualifiedPdfReader({ bookId, book }) {
  const api = useApi();
  const { isSignedIn } = useUser();
  const [token, setToken] = useState(null);
  const [error, setError] = useState("");
  const [signInRequired, setSignInRequired] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: progress, isFetched: progressFetched } = useQuery({
    queryKey: ["progress", bookId],
    queryFn: async () => (await api.get(`/books/${bookId}/progress`)).data,
    enabled: !!isSignedIn,
  });

  useEffect(() => {
    if (progress?.page) setCurrentPage(progress.page);
  }, [progress]);

  useEffect(() => {
    api.post(`/manuscripts/reader/session/${bookId}`)
      .then(({ data }) => setToken(data.token))
      .catch((e) => {
        if (e?.response?.status === 401) {
          setSignInRequired(true);
        } else {
          setError(e?.response?.data?.detail || "Purchase this edition to continue reading.");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const saveProgress = useMutation({
    mutationFn: async (page) => api.post(`/books/${bookId}/progress?page=${page}`),
  });

  if (signInRequired) {
    return (
      <div data-testid="reader-locked-message" className="text-center py-20">
        <p className="opacity-70 mb-4">Sign in and purchase this edition to continue reading.</p>
        <Link to="/sign-in" data-testid="reader-sign-in-cta" className="bg-white text-black px-5 py-2 text-sm uppercase tracking-wide inline-block">Sign In</Link>
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="reader-locked-message" className="text-center py-20">
        <p className="opacity-70 mb-4">{error}</p>
        <Link to={`/read/${bookId}`} className="underline text-sm">Back to book page</Link>
      </div>
    );
  }
  if (!token || (isSignedIn && !progressFetched)) return <div className="opacity-50 text-sm text-center py-20">Opening Reader…</div>;

  return (
    <>
      <PdfViewer
        fileUrl={`${API}/manuscripts/reader/view/${token}`}
        initialPage={progress?.page || 1}
        onPageChange={(page) => { setCurrentPage(page); if (isSignedIn) saveProgress.mutate(page); }}
        testIdPrefix="reader-pdf"
      />
      {isSignedIn && <AskMrE bookId={bookId} bookTitle={book.title} page={currentPage} />}
    </>
  );
}

export default function Reader() {
  const { bookId } = useParams();
  const api = useApi();
  const { isSignedIn } = useUser();
  const [activeChapter, setActiveChapter] = useState(null);
  const [fontSize, setFontSize] = useState(18);
  const [theme, setTheme] = useState("dark");
  const [selection, setSelection] = useState("");
  const contentRef = useRef(null);
  const scrollTimer = useRef(null);

  const { data: book } = useQuery({ queryKey: ["book", bookId], queryFn: async () => (await publicApi.get(`/books/${bookId}`)).data });
  const { data: progress } = useQuery({ queryKey: ["progress", bookId], queryFn: async () => (await api.get(`/books/${bookId}/progress`)).data, enabled: !!isSignedIn && !book?.reader_ready });

  useEffect(() => {
    if (book && !activeChapter) setActiveChapter(progress?.chapter_id || book.chapters[0]?.id);
  }, [book, progress, activeChapter]);

  const { data: chapter, error: chapterError } = useQuery({
    queryKey: ["chapter", bookId, activeChapter],
    queryFn: async () => (await publicApi.get(`/books/${bookId}/chapters/${activeChapter}`)).data,
    enabled: !!activeChapter,
    retry: false,
  });

  const saveProgress = useMutation({
    mutationFn: async (pct) => api.post(`/books/${bookId}/progress?chapter_id=${activeChapter}&position_percent=${pct}`),
  });

  useEffect(() => {
    if (isSignedIn && activeChapter && !book?.reader_ready) saveProgress.mutate(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChapter, isSignedIn, book?.reader_ready]);

  const addBookmark = useMutation({
    mutationFn: async () => api.post(`/books/${bookId}/bookmarks?chapter_id=${activeChapter}`),
    onSuccess: () => toast.success("Bookmark saved"),
  });

  const addHighlight = useMutation({
    mutationFn: async (text) => api.post(`/books/${bookId}/highlights?chapter_id=${activeChapter}&selected_text=${encodeURIComponent(text)}`),
    onSuccess: () => { toast.success("Highlight saved"); setSelection(""); },
  });

  const onScroll = () => {
    if (!isSignedIn) return;
    const el = contentRef.current;
    if (!el) return;
    clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      const pct = Math.min(100, Math.round((el.scrollTop / (el.scrollHeight - el.clientHeight || 1)) * 100));
      saveProgress.mutate(pct);
    }, 600);
  };

  const onMouseUp = () => {
    const sel = window.getSelection()?.toString()?.trim();
    setSelection(sel && sel.length > 3 ? sel : "");
  };

  const handleBookmark = () => {
    if (!isSignedIn) return toast.error("Sign in to save bookmarks.");
    addBookmark.mutate();
  };

  if (!book) return <div className="min-h-screen bg-black" />;

  if (book.is_bundle) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black text-white px-6" data-testid="reader-bundle-selector">
        <Link to={`/read/${bookId}`} data-testid="reader-close-button" className="absolute top-6 left-6 flex items-center gap-2 text-sm opacity-60 hover:opacity-100">
          <X className="w-4 h-4" /> Close
        </Link>
        <p className="text-xs uppercase tracking-widest opacity-40 mb-2">{book.title}</p>
        <p className="text-white/70 mb-10 text-center max-w-md">This bundle includes {book.bundle_components.length} titles. Choose one to open in the Reader.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl">
          {book.bundle_components.map((c) =>
            c.book_id && c.reader_ready ? (
              <Link key={c.book_id} to={`/reader/${c.book_id}`} data-testid={`reader-bundle-component-${c.book_id}`} className="flex flex-col items-center text-center group">
                <img src={c.cover_image} alt={c.title} className="w-28 aspect-[2/3] object-contain bg-white/5 group-hover:opacity-80 transition-opacity duration-200" />
                <span className="text-sm mt-3">{c.title}</span>
                <span className="text-[10px] uppercase tracking-wide text-brandAccent mt-1.5">Open in Reader →</span>
              </Link>
            ) : (
              <div key={c.book_id || c.title} data-testid={c.book_id ? `reader-bundle-component-${c.book_id}` : undefined} className="flex flex-col items-center text-center opacity-50">
                <img src={c.cover_image} alt={c.title} className="w-28 aspect-[2/3] object-contain bg-white/5" />
                <span className="text-sm mt-3">{c.title}</span>
                <span className="text-[10px] uppercase tracking-wide mt-1.5">Not yet in Reader</span>
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  if (!book.is_bundle && !book.reader_ready && (!book.chapters || book.chapters.length === 0)) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black text-white px-6" data-testid="reader-locked-message">
        <Link to={`/read/${bookId}`} data-testid="reader-close-button" className="absolute top-6 left-6 flex items-center gap-2 text-sm opacity-60 hover:opacity-100">
          <X className="w-4 h-4" /> Close
        </Link>
        <p className="text-xs uppercase tracking-widest opacity-40 mb-2">{book.title}</p>
        <p className="opacity-70 text-center max-w-md">{book.reader_customer_message || "This edition isn't available in the Reader yet."}</p>
      </div>
    );
  }

  if (book.reader_ready) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-black text-white" data-testid="ereader-view">
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/10">
          <Link to={`/read/${bookId}`} data-testid="reader-close-button" className="flex items-center gap-2 text-sm opacity-60 hover:opacity-100">
            <X className="w-4 h-4" /> Close
          </Link>
          <p className="text-xs uppercase tracking-widest opacity-40">{book.title}</p>
          <span className="w-16" />
        </div>
        <QualifiedPdfReader bookId={bookId} book={book} />
      </div>
    );
  }

  const chapterIndex = book.chapters.findIndex((c) => c.id === activeChapter);
  const colors = READING_THEMES[theme];

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background: colors.bg, color: colors.text }} data-testid="ereader-view">
      <aside className="w-60 border-r border-white/10 p-5 overflow-y-auto hidden md:block" style={{ borderColor: "rgba(128,128,128,0.2)" }}>
        <Link to={`/read/${bookId}`} data-testid="reader-close-button" className="flex items-center gap-2 text-sm opacity-60 hover:opacity-100 mb-6">
          <X className="w-4 h-4" /> Close
        </Link>
        <p className="text-xs uppercase tracking-widest opacity-40 mb-3">{book.title}</p>
        <ul className="space-y-2">
          {book.chapters.map((c) => (
            <li key={c.id}>
              <button
                data-testid={`reader-chapter-${c.chapter_number}`}
                onClick={() => setActiveChapter(c.id)}
                className={`text-sm text-left w-full ${c.id === activeChapter ? "opacity-100 font-medium" : "opacity-50 hover:opacity-80"}`}
              >
                {c.title}
              </button>
            </li>
          ))}
        </ul>
      </aside>


      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 border-b" style={{ borderColor: "rgba(128,128,128,0.2)" }}>
          <Link to={`/read/${bookId}`} className="md:hidden text-sm opacity-60"><X className="w-5 h-5" /></Link>
          <div className="flex items-center gap-3">
            <button data-testid="reader-font-decrease" onClick={() => setFontSize((f) => Math.max(14, f - 2))} className="opacity-60 hover:opacity-100"><Minus className="w-4 h-4" /></button>
            <span className="text-xs opacity-50">{fontSize}px</span>
            <button data-testid="reader-font-increase" onClick={() => setFontSize((f) => Math.min(28, f + 2))} className="opacity-60 hover:opacity-100"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-3">
            {Object.keys(READING_THEMES).map((t) => (
              <button key={t} data-testid={`reader-theme-${t}`} onClick={() => setTheme(t)} className={`text-xs uppercase tracking-wide ${theme === t ? "opacity-100 underline" : "opacity-50"}`}>{t}</button>
            ))}
            <button data-testid="reader-bookmark-button" onClick={handleBookmark} className="opacity-60 hover:opacity-100"><Bookmark className="w-4 h-4" /></button>
          </div>
        </div>

        <div ref={contentRef} onScroll={onScroll} onMouseUp={onMouseUp} className="flex-1 overflow-y-auto px-6 md:px-0">
          <div className="max-w-[65ch] mx-auto py-12" style={{ fontSize: `${fontSize}px`, lineHeight: 1.7 }}>
            {chapterError ? (
              <div data-testid="reader-locked-message" className="text-center py-20">
                <p className="opacity-70 mb-4">Purchase this edition to continue reading.</p>
                <Link to={`/read/${bookId}`} className="underline text-sm">Back to book page</Link>
              </div>
            ) : (
              <>
                <p data-testid="reader-preview-banner" className="text-[11px] border px-2 py-1.5 mb-6" style={{ opacity: 0.6, borderColor: "currentColor" }}>
                  Sample preview — this edition isn't fully available in the Reader yet.
                </p>
                <div className="[&>p]:mb-4" dangerouslySetInnerHTML={{ __html: chapter?.content_html || "" }} />
              </>
            )}
          </div>
        </div>

        {selection && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 text-sm flex items-center gap-3" data-testid="reader-highlight-toolbar">
            <span className="max-w-xs truncate">"{selection}"</span>
            <button data-testid="reader-highlight-save" onClick={() => addHighlight.mutate(selection)} className="underline">Highlight</button>
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: "rgba(128,128,128,0.2)" }}>
          <button
            data-testid="reader-prev-chapter"
            disabled={chapterIndex <= 0}
            onClick={() => setActiveChapter(book.chapters[chapterIndex - 1].id)}
            className="text-sm opacity-60 hover:opacity-100 disabled:opacity-20"
          >
            ← Previous
          </button>
          <AskAboutThis type="book_chapter" refId={activeChapter} title={chapter?.title || book.title} />
          <button
            data-testid="reader-next-chapter"
            disabled={chapterIndex >= book.chapters.length - 1}
            onClick={() => setActiveChapter(book.chapters[chapterIndex + 1].id)}
            className="text-sm opacity-60 hover:opacity-100 disabled:opacity-20"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
